/**
 * Compact, reusable safety-surface summary for contributed demos.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type React from "react";
import {
  assertContribDoctrineManifest,
  summarizeContribDoctrineManifest,
  type ContribDoctrineGateStatus,
} from "../domain/index.js";

export interface DoctrineManifestSnapshotProps {
  readonly manifest: unknown;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}

const STATUS_LABELS: Record<ContribDoctrineGateStatus, string> = {
  structural: "Structural",
  contained: "Contained",
  inert: "Inert",
  diagnostic: "Diagnostic",
};

const STATUS_COLORS: Record<ContribDoctrineGateStatus, string> = {
  structural: "#22c55e",
  contained: "#fbbf24",
  inert: "#38bdf8",
  diagnostic: "#a78bfa",
};

export function DoctrineManifestSnapshot({
  manifest,
  className,
  style,
}: DoctrineManifestSnapshotProps): React.JSX.Element {
  assertContribDoctrineManifest(manifest);
  const summary = summarizeContribDoctrineManifest(manifest);
  const stats = [
    { label: "Metrics", value: summary.metricCount },
    { label: "Guarantees", value: summary.guaranteeCount },
    { label: "Scenarios", value: summary.scenarioCount },
    { label: "Gates", value: summary.gateCount },
  ] as const;

  return (
    <section
      className={className}
      aria-label={`${manifest.title} safety surface`}
      style={{
        background: `linear-gradient(135deg, ${manifest.theme.surface}f2, ${manifest.theme.void}f5)`,
        border: `1px solid ${manifest.theme.border}`,
        borderRadius: 8,
        boxShadow: "0 18px 44px rgba(0,0,0,0.24)",
        color: manifest.theme.text,
        display: "grid",
        gap: 16,
        padding: 18,
        ...style,
      }}
    >
      <div
        style={{
          display: "grid",
          gap: 10,
          gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))",
        }}
      >
        <div>
          <p
            style={{
              color: manifest.theme.accent,
              fontFamily: manifest.theme.mono,
              fontSize: 11,
              letterSpacing: "0.08em",
              margin: 0,
              textTransform: "uppercase",
            }}
          >
            {manifest.ssmCoverage.map((ssm) => ssm.code).join(" · ")}
          </p>
          <h2
            style={{
              color: manifest.theme.bright,
              fontSize: 20,
              lineHeight: 1.2,
              margin: "6px 0 0",
            }}
          >
            Safety Surface
          </h2>
          <p
            style={{
              color: manifest.theme.text,
              fontSize: 13.5,
              lineHeight: 1.65,
              margin: "8px 0 0",
            }}
          >
            {manifest.tagline}
          </p>
        </div>

        <dl
          style={{
            display: "grid",
            gap: 8,
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            margin: 0,
          }}
        >
          {stats.map((stat) => (
            <div
              key={stat.label}
              style={{
                background: "rgba(255,255,255,0.045)",
                border: `1px solid ${manifest.theme.border}`,
                borderRadius: 8,
                padding: "9px 10px",
              }}
            >
              <dt
                style={{
                  color: manifest.theme.muted,
                  fontFamily: manifest.theme.mono,
                  fontSize: 10,
                  margin: 0,
                  textTransform: "uppercase",
                }}
              >
                {stat.label}
              </dt>
              <dd
                style={{
                  color: manifest.theme.bright,
                  fontFamily: manifest.theme.mono,
                  fontSize: 20,
                  margin: "2px 0 0",
                }}
              >
                {stat.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      <div
        aria-label={`${manifest.title} structural safety mechanisms`}
        style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
      >
        {manifest.ssmCoverage.map((ssm) => (
          <span
            key={ssm.code}
            title={ssm.role}
            style={{
              background: "rgba(255,255,255,0.045)",
              border: `1px solid ${manifest.theme.border}`,
              borderRadius: 8,
              color: manifest.theme.bright,
              fontFamily: manifest.theme.mono,
              fontSize: 11,
              padding: "5px 8px",
            }}
          >
            {ssm.code} · {ssm.name}
          </span>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gap: 12,
          gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))",
        }}
      >
        {manifest.guarantees.map((guarantee) => (
          <article
            key={guarantee.id}
            style={{
              background: "rgba(255,255,255,0.035)",
              border: `1px solid ${manifest.theme.border}`,
              borderRadius: 8,
              padding: 12,
            }}
          >
            <p
              style={{
                color: manifest.theme.accent,
                fontFamily: manifest.theme.mono,
                fontSize: 11,
                margin: 0,
              }}
            >
              {guarantee.id}
            </p>
            <h3
              style={{
                color: manifest.theme.bright,
                fontSize: 15,
                lineHeight: 1.3,
                margin: "5px 0 0",
              }}
            >
              {guarantee.security}
            </h3>
            <p
              style={{
                color: manifest.theme.text,
                fontSize: 12.5,
                lineHeight: 1.55,
                margin: "7px 0 0",
              }}
            >
              {guarantee.statement}
            </p>
          </article>
        ))}
      </div>

      <div
        aria-label={`${manifest.title} gate statuses`}
        style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
      >
        {manifest.gates.map((gate) => (
          <span
            key={gate.id}
            title={gate.invariant}
            style={{
              alignItems: "center",
              background: `${STATUS_COLORS[gate.status]}1a`,
              border: `1px solid ${STATUS_COLORS[gate.status]}88`,
              borderRadius: 999,
              color: manifest.theme.bright,
              display: "inline-flex",
              fontFamily: manifest.theme.mono,
              fontSize: 11,
              gap: 6,
              minHeight: 26,
              padding: "4px 9px",
              whiteSpace: "nowrap",
            }}
          >
            <span>{gate.id}</span>
            <span style={{ color: STATUS_COLORS[gate.status] }}>{STATUS_LABELS[gate.status]}</span>
          </span>
        ))}
      </div>
    </section>
  );
}
